import { useCallback, useEffect, useState } from "react";
import { useDebouncedValue } from "@mantine/hooks";
import { useForm } from "react-hook-form";
import { TextInput } from "react-hook-form-mantine";
import { Grid, Paper, Stack, Text, Button, Group } from "@mantine/core";
import { IconSend, IconDeviceFloppy } from "@tabler/icons-react";
import { isContentEmpty } from "@congrs/utils";
import { web_inviteCampaignSchema } from "@congrs/schemas";
import resolver from "@congrs/resolver";
import CampaignEditor from "../../Campaigns/CampaignEditor";
import PlaceholderMenu from "../../Campaigns/PlaceholderMenu";
import EmailPreview from "../../Campaigns/EmailPreview";
import { useConfirm } from "../../../hooks/useConfirm";
import { useGetEvent } from "../../../api/event";
import {
  useGetInviteCampaign,
  useGetInviteStats,
  useSaveInviteCampaign,
  useSendInvites,
} from "../../../api/invites";

// Compose + send the invite email. There is one invite campaign per event: it
// is loaded on mount, saved (created the first time, edited after), and sent to
// all imported recipients. Sending is queued to background jobs, so Send returns
// as soon as the recipients are claimed rather than waiting on the mailer.
// Self-contained (takes eventId) so it can be moved to its own route later
// without changes.
const InviteComposer = ({ eventId }) => {
  const confirm = useConfirm();
  const { data: event } = useGetEvent(eventId);
  const { data: campaign } = useGetInviteCampaign(eventId);
  const { data: stats } = useGetInviteStats(eventId);

  // Recipients still eligible for an initial send (imported, not yet sent).
  const sendableCount = Number(stats?.total ?? 0) - Number(stats?.sent ?? 0);

  const { control, handleSubmit, watch, setValue } = useForm({
    defaultValues: { subject: "", content: null, contentHtml: "" },
    resolver: resolver(web_inviteCampaignSchema),
  });

  const [previewContent, setPreviewContent] = useState(null);
  const [debouncedPreview] = useDebouncedValue(previewContent, 300);

  // Push the loaded campaign body into the editor once, via the version counter
  // (see CampaignEditor). Form fields are set alongside.
  const [appliedContent, setAppliedContent] = useState({
    content: null,
    version: 0,
  });

  useEffect(() => {
    if (!campaign) return;
    setValue("subject", campaign.subject || "");
    setValue("content", campaign.content || null);
    setValue("contentHtml", campaign.contentHtml || "");
    setPreviewContent(campaign.content || null);
    setAppliedContent((prev) => ({
      content: campaign.contentHtml || "",
      version: prev.version + 1,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaign?.id]);

  const subject = watch("subject");
  const contentHtml = watch("contentHtml");
  const isBodyEmpty = isContentEmpty(contentHtml);

  const { mutateAsync: saveCampaign, isPending: isSaving } =
    useSaveInviteCampaign(eventId);
  const { mutate: sendInvites, isPending: isSending } = useSendInvites(eventId);

  const handleContentChange = useCallback(
    ({ html, json }) => {
      setValue("content", json, { shouldDirty: true });
      setValue("contentHtml", html, { shouldDirty: true });
      setPreviewContent(json);
    },
    [setValue]
  );

  const hasContent = !!subject?.trim() && !isBodyEmpty;
  const canSend = hasContent && sendableCount > 0;

  const save = (data) =>
    saveCampaign({
      subject: data.subject,
      content: data.content,
      contentHtml: data.contentHtml,
    });

  const handleSave = handleSubmit(save);

  const doSend = handleSubmit(async (data) => {
    const saved = await save(data);
    sendInvites({ campaignId: String(saved.id) });
  });

  const handleSendClick = () => {
    confirm({
      title: "Send invites now?",
      message: `This will queue the invite email for ${sendableCount} recipient${
        sendableCount === 1 ? "" : "s"
      } and send it in the background. This cannot be undone.`,
      onConfirm: doSend,
    });
  };

  return (
    <Grid gap="lg">
      <Grid.Col span={{ base: 12, md: 7 }}>
        <Stack gap="md">
          <Paper withBorder shadow="md" p="md" radius="md">
            <TextInput
              label="Email subject"
              placeholder="e.g. You're invited to {{ eventName }}"
              control={control}
              name="subject"
              rightSection={
                <PlaceholderMenu
                  onInsert={(value) =>
                    setValue("subject", (subject || "") + value, {
                      shouldDirty: true,
                    })
                  }
                />
              }
            />
          </Paper>

          <Paper withBorder shadow="md" p="md" radius="md">
            <Text fw={500} mb="xs">
              Email content
            </Text>
            <CampaignEditor
              initialContent={null}
              onChange={handleContentChange}
              accentColor={event?.config?.accentColor}
              appliedContent={appliedContent.content}
              appliedVersion={appliedContent.version}
              registerLinkPreset
            />
          </Paper>

          <Group justify="flex-end">
            <Button
              variant="default"
              leftSection={<IconDeviceFloppy size={16} />}
              onClick={handleSave}
              disabled={!hasContent}
              loading={isSaving}
            >
              Save
            </Button>
            <Button
              leftSection={<IconSend size={16} />}
              onClick={handleSendClick}
              disabled={!canSend}
              loading={isSaving || isSending}
            >
              Send invites
            </Button>
          </Group>
          {sendableCount === 0 && (
            <Text size="sm" c="dimmed" ta="right">
              Import or add recipients to send invites.
            </Text>
          )}
        </Stack>
      </Grid.Col>

      <Grid.Col span={{ base: 12, md: 5 }}>
        <EmailPreview content={debouncedPreview} />
      </Grid.Col>
    </Grid>
  );
};

export default InviteComposer;
